/**
 * Lets a page suppress the site-wide marketing footer (COL-533).
 *
 * `App.tsx` mounts `<Footer />` after `<Routes>`, so it lands under EVERY
 * page. The V2 tenant dashboard carries its own footer from the design — a
 * dark band with the tenant's own links — and the marketing one underneath it
 * repeated the public sitemap, socials and a "book a viewing" CTA to someone
 * who already lives there.
 *
 * A path check in `Footer.tsx` cannot answer this: `/dashboard` renders V1 or
 * V2 depending on the feature flag, on `?dashboard=v1`, and on a latched
 * DocuSign/Stripe return that no longer shows in the URL. Only the component
 * that actually mounted knows. So the page declares it, and the shell reads it.
 *
 * A module-level store rather than a context, deliberately: a provider would
 * have to wrap both `<Routes>` and `<FooterShell />` in `App.tsx`, re-indenting
 * the whole route table and burying a four-line change in a 650-line
 * whitespace diff. There is one app and one footer, so one store is the right
 * altitude.
 */

import { useEffect, useSyncExternalStore } from "react";

/**
 * A COUNT, not a boolean.
 *
 * Under StrictMode an effect mounts, tears down and mounts again, and a
 * route change can mount the next page before the previous one unmounts. A
 * boolean would let the outgoing page's cleanup clear a flag the incoming
 * page had just set, and the marketing footer would reappear under it.
 */
let hiddenBy = 0;
const listeners = new Set<() => void>();

function publish() {
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Read by the shell in `App.tsx`. */
export function useSiteFooterHidden(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => hiddenBy > 0,
    () => false
  );
}

/**
 * Called by a page that brings its own footer.
 *
 * Releases on unmount, so navigating away from the dashboard — including the
 * hand-off to V1 — never leaves the rest of the site footless.
 */
export function useHideSiteFooter(): void {
  useEffect(() => {
    hiddenBy += 1;
    publish();
    return () => {
      hiddenBy -= 1;
      publish();
    };
  }, []);
}
